import { filterLinkedInLegacyPostFallback } from '@/biz/contentType';
import { BasePriceKeyListMap } from '@/interface/common';

export type CreatorContentTypePreviewPlacement = {
  platform: string;
  contentType?: string;
  allowedContentTypes?: string[];
  priceFields?: Array<{ contentType?: string }>;
  active?: boolean;
  raw?: { active?: boolean; priceFields?: unknown };
};

export const getCreatorPlacementAvailableContentTypes = (
  placement: CreatorContentTypePreviewPlacement
): string[] => {
  const priceFields: unknown =
    placement.priceFields ?? placement.raw?.priceFields;
  const candidates: unknown[] = placement.allowedContentTypes?.length
    ? placement.allowedContentTypes
    : Array.isArray(priceFields)
      ? priceFields.map((field) => field?.contentType)
      : [placement.contentType];
  const contentTypes = Array.from(
    new Set(
      candidates.map((value) => String(value ?? '').trim()).filter(Boolean)
    )
  );
  return filterLinkedInLegacyPostFallback(
    placement.platform,
    contentTypes,
    (contentType) => contentType
  );
};

// Only known aliases are equivalent. In particular, LinkedIn's legacy Post
// must not be mistaken for a new Image & Text or Video option.
export const getCreatorContentTypeKey = (platform: string, value?: string) => {
  const key = String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const aliases: Record<string, Record<string, string>> = {
    youtube: {
      youtubededicated: 'dedicatedvideo',
      youtubededicatedvideo: 'dedicatedvideo',
      youtubeintegrated: 'integratedvideo',
      youtubeintegratedvideo: 'integratedvideo'
    },
    instagram: {
      reel: 'reels',
      instagramreel: 'reels',
      instagramreels: 'reels',
      carouselpost: 'post',
      instagramcarouselpost: 'post'
    },
    tiktok: {
      tiktokvideo: 'video',
      tiktokshortvideo: 'video',
      shortvideo: 'video'
    },
    twitter: { twitterxpost: 'post', twitterpost: 'post', xpost: 'post' },
    linkedin: {
      imagepost: 'imagetext',
      linkedinimagepost: 'imagetext',
      videopost: 'video',
      linkedinvideopost: 'video',
      linkedinpost: 'post'
    }
  };
  return aliases[platform]?.[key] ?? key;
};

/** New channels follow campaign types; an unconfigured platform allows all its types. */
export const getNewCreatorChannelContentTypes = (
  platform: string,
  campaignPlacements: unknown
): string[] => {
  const mapping = BasePriceKeyListMap[platform] ?? {};
  const configured = new Set(
    Array.isArray(campaignPlacements) ? campaignPlacements : []
  );
  const campaignTypes = Object.entries(mapping)
    .filter(([, placement]) => configured.has(placement))
    .map(([contentType]) => contentType);
  return filterLinkedInLegacyPostFallback(
    platform,
    campaignTypes.length ? campaignTypes : Object.keys(mapping),
    (contentType) => contentType
  );
};
